/**
 * Displays pending adjustment requests with approve/reject actions
 */

import React, { useState } from "react";
import { formatDate } from '../../../utils/formatters';
import PropTypes from "prop-types";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Tooltip,
    TextField
} from "@mui/material";
import { DmsAltBox, DmsButton, DmsModal, intl, fetchData, HTTP_STATUS } from "dmsbasereact";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Swal from "sweetalert2";
import { StockAdjustmentType } from "../../../constants/StockAdjustmentType";

/**
 * @param {Array} adjustments - List of pending adjustments
 * @param {Function} onRefresh - Callback to refresh the adjustments list
 */
export const PendingAdjustmentsSection = ({ adjustments = [], onRefresh }) => {
    const [approveModalOpen, setApproveModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [selectedAdjustment, setSelectedAdjustment] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    const handleApproveClick = (adjustment) => {
        setSelectedAdjustment(adjustment);
        setApproveModalOpen(true);
    };

    const handleRejectClick = (adjustment) => {
        setSelectedAdjustment(adjustment);
        setRejectReason("");
        setRejectModalOpen(true);
    };

    const executeApprove = async () => {
        if (!selectedAdjustment) return;

        try {
            setActionLoading(true);
            const response = await fetchData(
                `/stock/adjustment/${selectedAdjustment.pk}/approve`,
                "POST",
                true,
                {}
            );

            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("AdjustmentApproved").d("Adjustment Approved"),
                    text: intl.get("AdjustmentApprovedMessage").d("The adjustment has been approved and stock has been updated."),
                });
                setApproveModalOpen(false);
                setSelectedAdjustment(null);
                if (onRefresh) onRefresh();
            } else {
                throw new Error(response.data?.detail || "Failed to approve adjustment");
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: err.response?.data?.detail || err.message || intl.get("FailedToApproveAdjustment").d("Failed to approve adjustment"),
            });
        } finally {
            setActionLoading(false);
        }
    };

    const executeReject = async () => {
        if (!selectedAdjustment || !rejectReason.trim()) {
            Swal.fire({
                icon: "warning",
                title: intl.get("ReasonRequired").d("Reason Required"),
                text: intl.get("PleaseProvideRejectionReason").d("Please provide a reason for rejection."),
            });
            return;
        }

        if (rejectReason.trim().length < 10) {
            Swal.fire({
                icon: "warning",
                title: intl.get("ReasonTooShort").d("Reason Too Short"),
                text: intl.get("RejectionReasonMinLength").d("Rejection reason must be at least 10 characters."),
            });
            return;
        }

        try {
            setActionLoading(true);
            const response = await fetchData(
                `/stock/adjustment/${selectedAdjustment.pk}/reject`,
                "POST",
                true,
                { rejection_reason: rejectReason }
            );

            if (response.status === HTTP_STATUS.OK_200) {
                Swal.fire({
                    icon: "success",
                    title: intl.get("AdjustmentRejected").d("Adjustment Rejected"),
                    text: intl.get("AdjustmentRejectedMessage").d("The adjustment has been rejected."),
                });
                setRejectModalOpen(false);
                setSelectedAdjustment(null);
                setRejectReason("");
                if (onRefresh) onRefresh();
            } else {
                throw new Error(response.data?.detail || "Failed to reject adjustment");
            }
        } catch (err) {
            Swal.fire({
                icon: "error",
                title: intl.get("Error").d("Error"),
                text: err.response?.data?.detail || err.message || intl.get("FailedToRejectAdjustment").d("Failed to reject adjustment"),
            });
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <>
            <DmsAltBox.Card>
            <DmsAltBox.Header>
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                        <PendingActionsIcon color="primary" />
                        <Typography variant="h6">
                            {intl.get("PendingAdjustments").d("Pending Adjustments")}
                        </Typography>
                    </Box>
                    <Chip
                        label={adjustments.length}
                        color={adjustments.length > 0 ? "warning" : "default"}
                        size="small"
                    />
                </Box>
            </DmsAltBox.Header>
            <DmsAltBox.Body>
                {adjustments.length === 0 ? (
                    <Typography color="textSecondary" textAlign="center" py={4}>
                        {intl.get("NoPendingAdjustments").d("No pending adjustments")}
                    </Typography>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{intl.get("Batch").d("Batch")}</TableCell>
                                    <TableCell>{intl.get("Product").d("Product")}</TableCell>
                                    <TableCell>{intl.get("Type").d("Type")}</TableCell>
                                    <TableCell align="right">{intl.get("SystemQty").d("System")}</TableCell>
                                    <TableCell align="right">{intl.get("PhysicalQty").d("Physical")}</TableCell>
                                    <TableCell align="right">{intl.get("Adjustment").d("Adjustment")}</TableCell>
                                    <TableCell>{intl.get("RequestedBy").d("Requested By")}</TableCell>
                                    <TableCell>{intl.get("Date").d("Date")}</TableCell>
                                    <TableCell align="center">{intl.get("Actions").d("Actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {adjustments.map((adj) => (
                                    <TableRow key={adj.pk}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {adj.batch_number || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{adj.part_name || "-"}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={StockAdjustmentType.getText(
                                                    typeof adj.adjustment_type === 'object'
                                                        ? adj.adjustment_type.value
                                                        : adj.adjustment_type
                                                )}
                                                color={StockAdjustmentType.getColor(
                                                    typeof adj.adjustment_type === 'object'
                                                        ? adj.adjustment_type.value
                                                        : adj.adjustment_type
                                                )}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">{adj.system_quantity}</TableCell>
                                        <TableCell align="right">{adj.physical_quantity}</TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                label={
                                                    adj.adjustment_quantity > 0
                                                        ? `+${adj.adjustment_quantity}`
                                                        : adj.adjustment_quantity
                                                }
                                                color={adj.adjustment_quantity > 0 ? "success" : "error"}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>{adj.requested_by_name || "-"}</TableCell>
                                        <TableCell>{formatDate(adj.created_on)}</TableCell>
                                        <TableCell align="center">
                                                <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
                                                    <Tooltip title={intl.get("Approve").d("Approve")}>
                                                        <IconButton
                                                            size="small"
                                                            color="success"
                                                            onClick={() => handleApproveClick(adj)}
                                                        >
                                                            <CheckCircleIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={intl.get("Reject").d("Reject")}>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleRejectClick(adj)}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DmsAltBox.Body>
        </DmsAltBox.Card>

        {/* Approval Confirmation Modal */}
        <DmsModal
            isVisible={approveModalOpen}
            onClose={() => {
                setApproveModalOpen(false);
                setSelectedAdjustment(null);
            }}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={intl.get("ConfirmApproval").d("Confirm Approval")} />
            <DmsModal.Body>
                <Box sx={{ p: 2 }}>
                    <Typography variant="body1" gutterBottom>
                        {intl.get("ConfirmAdjustmentApprovalMessage").d("Are you sure you want to approve this stock adjustment?")}
                    </Typography>
                    {selectedAdjustment && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{intl.get("Batch").d("Batch")}:</strong> {selectedAdjustment.batch_number}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{intl.get("Product").d("Product")}:</strong> {selectedAdjustment.part_name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{intl.get("Type").d("Type")}:</strong> {StockAdjustmentType.getText(
                                    typeof selectedAdjustment.adjustment_type === 'object'
                                        ? selectedAdjustment.adjustment_type.value
                                        : selectedAdjustment.adjustment_type
                                )}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{intl.get("SystemQty").d("System")}:</strong> {selectedAdjustment.system_quantity}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{intl.get("PhysicalQty").d("Physical")}:</strong> {selectedAdjustment.physical_quantity}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{intl.get("Adjustment").d("Adjustment")}:</strong>{" "}
                                <Chip
                                    label={
                                        selectedAdjustment.adjustment_quantity > 0
                                            ? `+${selectedAdjustment.adjustment_quantity}`
                                            : selectedAdjustment.adjustment_quantity
                                    }
                                    color={selectedAdjustment.adjustment_quantity > 0 ? "success" : "error"}
                                    size="small"
                                />
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        {intl.get("ApprovalConsequenceAdjustment").d("After approval, the stock quantity will be updated to match the physical count.")}
                    </Typography>
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={() => {
                            setApproveModalOpen(false);
                            setSelectedAdjustment(null);
                        }}
                        disabled={actionLoading}
                    />
                    <DmsButton
                        variant="primary"
                        text={intl.get("Approve").d("Approve")}
                        onClick={executeApprove}
                        disabled={actionLoading}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>

        {/* Rejection Modal */}
        <DmsModal
            isVisible={rejectModalOpen}
            onClose={() => {
                setRejectModalOpen(false);
                setSelectedAdjustment(null);
                setRejectReason("");
            }}
            maxWidth="sm"
            fullWidth
        >
            <DmsModal.Header title={intl.get("RejectAdjustment").d("Reject Adjustment")} />
            <DmsModal.Body>
                <Box sx={{ p: 2 }}>
                    <Typography variant="body2" gutterBottom>
                        {intl.get("RejectAdjustmentMessage").d("Please provide a reason for rejecting this adjustment (minimum 10 characters):")}
                    </Typography>
                    {selectedAdjustment && (
                        <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>{intl.get("Batch").d("Batch")}:</strong> {selectedAdjustment.batch_number} | <strong>{intl.get("Product").d("Product")}:</strong> {selectedAdjustment.part_name}
                            </Typography>
                            <Typography variant="body2">
                                <strong>{intl.get("Adjustment").d("Adjustment")}:</strong>{" "}
                                {selectedAdjustment.adjustment_quantity > 0 ? "+" : ""}{selectedAdjustment.adjustment_quantity}
                            </Typography>
                        </Box>
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        placeholder={intl.get("EnterRejectionReason").d("Enter rejection reason...")}
                        error={rejectReason.length > 0 && rejectReason.length < 10}
                        helperText={
                            rejectReason.length > 0 && rejectReason.length < 10
                                ? intl.get("MinCharsRequired", { min: 10 }).d("Minimum 10 characters required")
                                : ""
                        }
                    />
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                    <DmsButton
                        variant="secondary"
                        text={intl.get("Cancel").d("Cancel")}
                        onClick={() => {
                            setRejectModalOpen(false);
                            setSelectedAdjustment(null);
                            setRejectReason("");
                        }}
                        disabled={actionLoading}
                    />
                    <DmsButton
                        variant="danger"
                        text={intl.get("Reject").d("Reject")}
                        onClick={executeReject}
                        disabled={actionLoading || rejectReason.trim().length < 10}
                    />
                </Box>
            </DmsModal.Footer>
        </DmsModal>
        </>
    );
};

PendingAdjustmentsSection.propTypes = {
    adjustments: PropTypes.arrayOf(
        PropTypes.shape({
            pk: PropTypes.number.isRequired,
            batch_number: PropTypes.string,
            part_name: PropTypes.string,
            adjustment_type_name: PropTypes.string,
            system_quantity: PropTypes.number,
            physical_quantity: PropTypes.number,
            adjustment_quantity: PropTypes.number,
            requested_by_name: PropTypes.string,
            created_on: PropTypes.string
        })
    ),
    onRefresh: PropTypes.func
};

